import { Panel } from 'primeng/panel';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { Checkbox } from 'primeng/checkbox';
import { FormsModule } from '@angular/forms';
import { RadioButton } from 'primeng/radiobutton';
import { AutoFocusModule } from 'primeng/autofocus';
import { ChangeDetectionStrategy, Component, effect, inject, input, output, signal } from '@angular/core';

import { RoleCode } from '../../models/user/user.enums';
import { RoleColorsUtil } from '../../utils/role-colors.util';
import { ROLE_OPTIONS } from '../../models/user/user.constants';
import { AuthService } from '../../../core/services/auth.service';

@Component({
	selector: 'app-role-selection-dialog',
	imports: [Dialog, Button, RadioButton, Checkbox, FormsModule, AutoFocusModule, Panel],
	templateUrl: './role-selection-dialog.component.html',
	styleUrl: './role-selection-dialog.component.css',
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class RoleSelectionDialogComponent {
	public readonly visible = input.required<boolean>();
	public readonly visibleChange = output<boolean>();

	public selectedRole = signal<RoleCode | undefined>(undefined);
	public rememberChoice = signal<boolean>(false);

	private readonly authService = inject(AuthService);
	public readonly userRoles = signal<RoleCode[]>([]);
	public readonly ROLE_OPTIONS = ROLE_OPTIONS;

	constructor() {
		effect(() => {
			if (this.visible()) {
				this.loadUserRoles();
				this.setInitialRole();
			}
		});
	}

	/**
	 * Loads the user's available roles
	 */
	private loadUserRoles(): void {
		const roles = this.authService.getRoles();
		this.userRoles.set(roles);
	}

	/**
	 * Sets the initial selected role based on current active role or first available role
	 */
	private setInitialRole(): void {
		const activeRole = this.authService.getActiveRole();
		const roles = this.userRoles();

		if (activeRole && roles.includes(activeRole)) {
			this.selectedRole.set(activeRole);
		} else if (roles.length > 0) {
			this.selectedRole.set(roles[0]);
		}
	}

	/**
	 * Handles the confirm button click
	 */
	public onConfirm(): void {
		const role = this.selectedRole();
		if (!role) return;
		this.authService.setActiveRole(role);
		if (this.rememberChoice()) {
			localStorage.setItem('isShowSelectRoleDialog', 'false');
		}

		this.closeDialog();
		this.reloadPage();
	}

	/**
	 * Handles the cancel button click
	 */
	public onCancel(): void {
		this.closeDialog();
	}

	/**
	 * Closes the dialog
	 */
	private closeDialog(): void {
		this.visibleChange.emit(false);
	}

	/**
	 * Reloads the current page
	 */
	private reloadPage(): void {
		window.location.reload();
	}

	/**
	 * Gets the color for a specific role
	 */
	public getRoleColor(role: RoleCode): string {
		return RoleColorsUtil.getRoleColor(role);
	}

	/**
	 * Handles role selection change
	 */
	public onRoleChange(role: RoleCode): void {
		this.selectedRole.set(role);
	}

	/**
	 * Handles remember choice checkbox change
	 */
	public onRememberChoiceChange(checked: boolean): void {
		this.rememberChoice.set(checked);
	}
}
